import { useEffect, useState, useRef } from "react";
import { Form, Table } from "react-bootstrap";
import useApp from '../AppContext';
import { loadListObject, loadDataObject } from "./CalendarUtils";
import PaginationControls from "../Components/PaginationControls";
import { t } from "./../i18n";
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import RowsPerPageSelector from '../Components/RowsPerPageSelector';

function ListSuppliers(props) {
    const app = useApp();
    const searchInputRef = useRef(null);
    const [serverUrl] = useState(app.serverUrl);
    const [userid] = useState(app.sessionToken?.UserId);
    const [data, setData] = useState([]);
    const [allColumnsObj, setAllColumnsObj] = useState({});
    const [isLoad, setIsLoad] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [isDebouncing, setIsDebouncing] = useState(false);

    useEffect(() => {
        if (props.showModal && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [props.showModal]);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    useEffect(() => {
        async function getDefaultLayouts() {
            if (!serverUrl) return;

            if (!allColumnsObj || (Object.keys(allColumnsObj).length === 0)) {
                const objectName = "Yms_Suppliers";
                const allobj = await loadListObject(serverUrl, props.userToken, objectName);
                setAllColumnsObj(prev => ({ ...prev, [objectName]: allobj }));
            }
        }
        getDefaultLayouts();
    }, [serverUrl, props.userToken, allColumnsObj]);

    const fetchSuppliers = async (searchText = '') => {
        if (!userid || !app.sessionToken?.Role || Object.keys(allColumnsObj).length === 0) return;

        const objectName = "Yms_Suppliers";

        let defaultVariables = {
            filterName: "customfilter",
        };

        let prms = [];
        let prmValues = {};
        let baseFilter = "";
        if (app.sessionToken.Role !== "DoorUser" && app.sessionToken.Role !== "Admin" && app.sessionToken.Role !== "Planner") {
            baseFilter = "YmsSup.SUPPLIERID in (SELECT NVL(YMS_USER.SUPPLIERID,YMS3SU.SUPPLIERID) " +
                "FROM YMS_USERS YMS_USER " +
                "LEFT JOIN YMS_3PLS YMS3PL1 ON YMS3PL1.YMS3PLID = YMS_USER.YMS3PLID " +
                "LEFT JOIN YMS_3PLSUPPLIERS YMS3SU ON YMS3SU.YMS3PLID =YMS3PL1.YMS3PLID AND YMS3SU.ACTIVEFLG=1 " +
                "WHERE YMS_USER.USERID =?(;n;5;p_UserId))";

            prms.push({ parameterName: "p_UserId", type: "" });
            prmValues["p_UserId"] = userid;
        }
        if (searchText && searchText.trim() !== '') {
            const searchFilter = "(UPPER(YmsSup.SUPPLIERCODE) LIKE UPPER(?(;s;;p_SearchTerm)) OR " +
                "UPPER(YmsSup.NAME1) LIKE UPPER(?(;s;;p_SearchTerm2)) OR " +
                "UPPER(YmsSup.SOCIALSECURITYNO) LIKE UPPER(?(;s;;p_SearchTerm3)) OR " +
                "UPPER(YmsSup.NAMESHORT) LIKE UPPER(?(;s;;p_SearchTerm4)))";

            if (baseFilter) {
                defaultVariables["appendFilter"] = `(${baseFilter}) AND ${searchFilter}`;
            } else {
                defaultVariables["appendFilter"] = searchFilter;
            }

            const searchPattern = `%${searchText.trim()}%`;
            prms.push({ parameterName: "p_SearchTerm", type: "" });
            prmValues["p_SearchTerm"] = searchPattern;
            prms.push({ parameterName: "p_SearchTerm2", type: "" });
            prmValues["p_SearchTerm2"] = searchPattern;
            prms.push({ parameterName: "p_SearchTerm3", type: "" });
            prmValues["p_SearchTerm3"] = searchPattern;
            prms.push({ parameterName: "p_SearchTerm4", type: "" });
            prmValues["p_SearchTerm4"] = searchPattern;
        } else if (baseFilter) {
            defaultVariables["appendFilter"] = baseFilter;
        }

        try {
            const loadingState = searchText ? setIsSearching : setIsLoad;
            loadingState(true);

            const responseObj = await loadDataObject(
                serverUrl,
                props.userToken,
                objectName,
                allColumnsObj,
                prms,
                prmValues,
                defaultVariables
            );

            if (responseObj) {
                setData(responseObj);
                setIsDataFetched(true);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error fetching suppliers:", error);
        } finally {
            const loadingState = searchText ? setIsSearching : setIsLoad;
            loadingState(false);
        }
    };

    const fetchAllSuppliers = () => fetchSuppliers('');

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        if (value.trim() === '') {
            setData([]);
            setIsDataFetched(false);
            setCurrentPage(1);
            setIsDebouncing(false);
            return;
        }
        setIsDebouncing(true);

        const newTimeout = setTimeout(() => {
            setIsDebouncing(false);
            fetchSuppliers(value);
        }, 2000);

        setSearchTimeout(newTimeout);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchTerm.trim() !== '') {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            setIsDebouncing(false);
            fetchSuppliers(searchTerm);
        }
    };

    return (
        <>

            <div className="p-4">
                <div className="list-modal-grid">
                    <div className="list-modal-left">
                        {data.length > 0 && data.filter(supplier => props.findSelectSupplier && props.findSelectSupplier(supplier)).length > 1 && (
                            <button
                                onClick={() => {
                                    data.forEach(supplier => {
                                        if (props.findSelectSupplier && props.findSelectSupplier(supplier)) {
                                            props.handleSelectSupplier(supplier);
                                        }
                                    });
                                }}
                                className="btn btn-outline-secondary"
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '14px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500'
                                }}
                            >
                                <RemoveDoneIcon style={{ fontSize: '15px' }} />
                                {t('common.deselectAll', props.language)}
                            </button>
                        )}
                    </div>

                    <div className="list-modal-center">
                        <div className="searchBar-wrapper">
                            <i className={`bi ${isSearching ? 'bi-arrow-clockwise' : 'bi-search'}`}
                                style={{
                                    animation: isSearching ? 'spin 1s linear infinite' : 'none',
                                    color: isSearching ? 'var(--color-main-background)' : 'inherit'
                                }} />
                            <input
                                type="text"
                                placeholder={t('common.search', props.language)}
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                                ref={searchInputRef}
                            />
                            {searchTerm && (
                                <i
                                    className="bi bi-x-circle"
                                    onClick={() => handleSearchChange('')}
                                />
                            )}
                        </div>

                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={fetchAllSuppliers}
                                disabled={isLoad || Object.keys(allColumnsObj).length === 0}
                                className="btn btn-primary"
                                style={{
                                    padding: '3px 7px',
                                    fontSize: '19px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--color-main-background)',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {isLoad ? (
                                    <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 1s linear infinite' }}></i>
                                ) : (
                                    <i className="bi bi-arrow-clockwise"></i>
                                )}
                            </button>
                            {Object.keys(allColumnsObj).length === 0 && (
                                <p style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px', color: '#666', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    Loading metadata...
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="list-modal-right">
                        <RowsPerPageSelector
                            isDataFetched={isDataFetched}
                            dataLength={data.length}
                            itemsPerPage={itemsPerPage}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            language={props.language}
                        />
                    </div>

                    <div className="list-modal-mobile-rows">
                        <RowsPerPageSelector
                            isDataFetched={isDataFetched}
                            dataLength={data.length}
                            itemsPerPage={itemsPerPage}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            language={props.language}
                        />
                    </div>


                </div>
            </div>


            <div style={{
                minHeight: "250px",
                // maxHeight: itemsPerPage <= 5 ? "250px" : itemsPerPage <= 10 ? "450px" : "600px",
                borderRadius: '22px',
                overflowY: "auto",
                overflowX: "hidden"
            }}>
                <Table striped hover className='table-custom-pos'>
                    <thead>
                        <tr>
                            <th>{t('listSuppliers.code', props.language)}</th>
                            <th>{t('listSuppliers.supplier', props.language)}</th>
                            <th>{t('listSuppliers.nameShort', props.language)}</th>
                            <th>{t('listSuppliers.vat', props.language)}</th>
                            <th>{t('common.select', props.language)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isSearching || isDebouncing ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                                    <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}></i>
                                    {t('common.search', props.language)}...
                                </td>
                            </tr>
                        ) : currentItems.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                                    {searchTerm && !isDebouncing ? (
                                        <div>
                                            {t('common.noData', props.language)}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', width: '375px', margin: 'auto', justifyContent: 'center', gap: '8px', flexDirection: 'column' }}>
                                            <i className="bi bi-info-circle" style={{ fontSize: '40px', color: 'var(--color-main-background)' }}></i>
                                            {t('listSuppliers.clickToFetch', props.language)}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((supplier) => {
                                const isSelected = props.findSelectSupplier ? Boolean(props.findSelectSupplier(supplier)) : false;
                                return (
                                    <tr
                                        key={supplier.SupplierId}
                                        onClick={() => props.handleSelectSupplier(supplier)}
                                        style={{
                                            cursor: 'pointer',
                                            borderLeft: isSelected ? '5px solid var(--color-main-background)' : '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <td>#{supplier.SupplierCode}</td>
                                        <td>{supplier.Name1}</td>
                                        <td>{supplier.NameShort || '-'}</td>
                                        <td>{supplier.SocialSecurityNo}</td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                className='custom-checkbox'
                                                name="supplierSelection"
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => props.handleSelectSupplier(supplier)}
                                                checked={isSelected}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>


            {data.length > itemsPerPage && (
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                />
            )}

            <div className="button-save-pos">
                <button onClick={props.handleSaveSuppliers}>
                    <i className="bi bi-save me-1"></i> {t('common.select', props.language)}
                </button>
            </div>
        </>
    );
}

export default ListSuppliers;
